"use client";

// src/ui/components/calculator/sticky-pill.tsx
//
// Mobile-only (<lg) bottom-anchored "live result" pill. When the Cockpit scrolls
// out of view UPWARD, this pill fades in at the bottom of the viewport so the
// live draw-up figures stay glued to the thumb zone (72% of users are mobile).
// Tapping it smooth-scrolls back to the Cockpit.
//
// Anchored at the BOTTOM by design: the top is already occupied by the sticky
// header (top:0) and the sticky tab-nav (top:60) — anchoring the pill at the top
// would collide with both. See design_handoff/03-interactions-state.md §Sticky.
//
// z/top map: header 0 → tabnav 60 → cockpit sticky 84 (desktop) → pill bottom 14 z-40.

import { useEffect, useState } from "react";

interface StickyPillProps {
	/** id of the Cockpit element to observe (e.g. "pr-cockpit"). */
	cockpitId: string;
	/** Live insulin-unit reading (rounded for display by the caller's result). */
	insulinUnits: number;
	/** Live draw-up volume in ml. */
	volume: number;
	/** Per-peptide accent class for the IU figure (e.g. "text-primary"). */
	accentClassName: string;
	/** "live" label (localized by caller). */
	liveLabel: string;
	/** Formatted "€/Dosis" label, or null/undefined to render an em-dash. */
	pricePerDoseLabel?: string | null;
	locale: string;
}

/**
 * Visible only below `lg`. Uses an IntersectionObserver on the Cockpit element so
 * it appears precisely when the result panel leaves the top of the viewport —
 * robust on tall layouts where a fixed scrollY threshold would misfire.
 */
export function StickyPill({
	cockpitId,
	insulinUnits,
	volume,
	accentClassName,
	liveLabel,
	pricePerDoseLabel,
	locale,
}: StickyPillProps) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const node = document.getElementById(cockpitId);
		if (!node) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				// Show only when the cockpit has scrolled UP past the top edge —
				// not when the user hasn't reached it yet (scrolled below the fold).
				setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
			},
			{ threshold: 0 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [cockpitId]);

	const handleTap = () => {
		const node = document.getElementById(cockpitId);
		node?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const hasResult = volume > 0;
	const iu = Math.round(insulinUnits);
	const volumeLabel = new Intl.NumberFormat(locale, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(volume);

	return (
		<button
			type="button"
			onClick={handleTap}
			aria-hidden={!visible}
			tabIndex={visible ? 0 : -1}
			aria-label={liveLabel}
			className={[
				"fixed inset-x-3 bottom-[14px] z-40 lg:hidden",
				"border-primary/60 bg-card/95 flex min-h-12 items-center gap-2 rounded-full border px-3.5 py-2.5 shadow-lg backdrop-blur xs:gap-2.5 xs:px-4",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				"motion-safe:duration-[0.22s] motion-safe:transition-[opacity,transform]",
				visible
					? "pointer-events-auto translate-y-0 opacity-100"
					: "pointer-events-none translate-y-3 opacity-0",
			].join(" ")}
		>
			{/* Live indicator: the pulsing dot always shows; the "live" word is hidden on the
			    narrowest phones (the dot carries the meaning) so IU + ml + €/Dosis fit on one
			    line down to 360px. The dot keeps the liveLabel via the button's aria-label. */}
			<span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.13em] text-primary">
				<span
					aria-hidden
					className="h-2 w-2 rounded-full bg-success motion-safe:animate-[pr-pulse_2s_infinite]"
				/>
				<span className="hidden xs:inline">{liveLabel}</span>
			</span>
			<span className={`shrink-0 font-mono text-sm font-bold tabular-nums ${accentClassName}`}>
				{hasResult ? `${iu} IU` : "—"}
			</span>
			<span aria-hidden className="shrink-0 text-border">
				·
			</span>
			<span className="shrink-0 font-mono text-sm tabular-nums text-muted-foreground">
				{hasResult ? `${volumeLabel} ml` : "—"}
			</span>
			{pricePerDoseLabel && (
				<>
					<span aria-hidden className="shrink-0 text-border">
						·
					</span>
					<span className="min-w-0 flex-1 truncate font-mono text-sm tabular-nums text-muted-foreground">
						{pricePerDoseLabel}
					</span>
				</>
			)}
			<span aria-hidden className="ml-auto shrink-0 text-base leading-none text-muted-foreground">
				↑
			</span>
		</button>
	);
}
